<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'
import { useProjectStore } from '@/stores/project'
import { useOrgStore } from '@/stores/org'
import { useUserStore } from '@/stores/user'
import { useRouter } from 'vue-router'
import { Search, X } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toast } from '@/components/ui/toast'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import UsersList from './UsersList.vue'
import { API, HOST } from '@/utils/http'
import { debounce } from 'lodash'
import AdminNavigation from '@/components/admin/AdminNavigation.vue'

const router = useRouter()
const projectStore = useProjectStore()
const orgStore = useOrgStore()
const userStore = useUserStore()
const member = computed(() => userStore.member)

const users = ref([])
const loading = ref(false)
const totalUsers = ref(0)
const currentPage = ref(1)
const pageSize = ref(50)

const searchQuery = ref('')
const selectedOrg = ref<string | null>(null)
const selectedProject = ref<string | null>(null)
const showFilters = ref(true)
interface Organization {
  organization_id: number
  organization_name: string
}

interface Project {
  project_id: number
  project_name: string
}

const organizations = ref<Organization[]>([])
const projects = ref<Project[]>([])

const fetchUsers = async () => {
  loading.value = true
  try {
    const params = new URLSearchParams()
    params.append('limit', pageSize.value.toString())
    params.append('offset', ((currentPage.value - 1) * pageSize.value).toString())

    if (searchQuery.value) {
      params.append('search', searchQuery.value)
    }
    if (selectedOrg.value) {
      params.append('organization_id', selectedOrg.value)
    }
    if (selectedProject.value) {
      params.append('project_id', selectedProject.value)
    }

    const response = await API({
      method: 'GET',
      url: `${HOST}/admin/users?${params.toString()}`
    })

    if (response.data) {
      users.value = response.data.users
      totalUsers.value = response.data.total
    }
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'
    toast({
      title: `Failed to fetch users: ${errorMessage}`,
      variant: 'destructive'
    })
  } finally {
    loading.value = false
  }
}

const debouncedSearch = debounce(() => {
  currentPage.value = 1
  fetchUsers()
}, 500)

const clearFilters = () => {
  searchQuery.value = ''
  selectedOrg.value = null
  selectedProject.value = null
  currentPage.value = 1
  fetchUsers()
}

const totalPages = computed(() => Math.ceil(totalUsers.value / pageSize.value))

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++
    fetchUsers()
  }
}

const prevPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--
    fetchUsers()
  }
}

const handleUserUpdated = () => {
  fetchUsers()
}

watch(searchQuery, () => {
  debouncedSearch()
})

watch([selectedOrg, selectedProject], () => {
  currentPage.value = 1
  fetchUsers()
})

watch(() => member.value, (value) => {
  if (!value) {
    return
  }
  if (!value.is_super_user) {
    router.push({name: 'Dashboard'})
  }
})

const fetchOrganizations = async () => {
  try {
    await orgStore.fetchOrgs()
    organizations.value = orgStore.orgs || []
  } catch (error) {
    console.error('Failed to fetch organizations:', error)
  }
}

const fetchProjects = async () => {
  if (selectedOrg.value) {
    try {
      await projectStore.fetchProjects()
      projects.value = projectStore.projects || []
    } catch (error) {
      console.error('Failed to fetch projects:', error)
    }
  } else {
    projects.value = []
  }
}

onMounted(async () => {
  if (member.value && !member.value.is_super_user) {
    router.push({name: 'Dashboard'})
    return
  }
  await fetchOrganizations()
  fetchUsers()
})

watch(selectedOrg, () => {
  selectedProject.value = null
  fetchProjects()
})
</script>

<template>
  <div v-if="member && member.is_super_user" class="h-full flex flex-col">
    <AdminNavigation />
    <div class="px-2 py-2">
      <div class="flex items-center justify-between mb-4">
        <div class="flex items-center gap-2">
          <h1 class="text-xl font-bold">Members</h1>
        </div>
        <div class="text-sm text-muted-foreground">
          {{ totalUsers }} total members
        </div>
      </div>

      <div class="space-y-3">
        <div class="flex items-center gap-2">
          <div class="relative flex-1">
            <Search class="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              v-model="searchQuery"
              placeholder="Search members by name or email..."
              class="pl-10"
            />
          </div>
        </div>

        <div>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <Label class="text-sm mb-1">Filter by Organization</Label>
              <Select v-model="selectedOrg">
                <SelectTrigger>
                  <SelectValue placeholder="Select organization..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>Organizations</SelectLabel>
                    <SelectItem
                      v-for="org in organizations"
                      :key="org.organization_id"
                      :value="org.organization_id.toString()"
                    >
                      {{ org.organization_name }}
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label class="text-sm mb-1">Filter by Project</Label>
              <Select v-model="selectedProject" :disabled="!selectedOrg">
                <SelectTrigger>
                  <SelectValue placeholder="Select project..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>Projects</SelectLabel>
                    <SelectItem
                      v-for="project in projects"
                      :key="project.project_id"
                      :value="project.project_id.toString()"
                    >
                      {{ project.project_name }}
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div class="flex justify-end">
            <Button variant="ghost" size="sm" @click="clearFilters">
              <X class="h-3 w-3 mr-1" />
              Clear filters
            </Button>
          </div>
        </div>
      </div>
    </div>

    <div class="flex-1 overflow-auto p-2">
      <UsersList
        :users="users"
        :loading="loading"
        @user-updated="handleUserUpdated"
      />
    </div>

    <div v-if="totalPages > 1" class="px-4 py-3 border-t flex items-center justify-between">
      <div class="text-sm text-muted-foreground">
        Page {{ currentPage }} of {{ totalPages }}
      </div>
      <div class="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          @click="prevPage"
          :disabled="currentPage === 1"
        >
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          @click="nextPage"
          :disabled="currentPage === totalPages"
        >
          Next
        </Button>
      </div>
    </div>
  </div>
</template>
